import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { CLEAR_AUTH_EVENT } from '../constants/authEvents';
import api, { attemptSilentRefresh } from '../services/api';
import posthog from '../config/posthog';

function isPasswordRecoveryPath(path: string): boolean {
  return path === '/forgot-password' || path.startsWith('/reset-password');
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  avatar?: string;
  phoneNumber?: string;
}

interface AuthContextType {
  user: User | null;
  login: (token: string, user: User) => void;
  updateProfile: (token: string, user: User) => void;
  logout: () => void;
  isAuthenticated: boolean;
  token: string | null;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const hydratedRef = useRef(false);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    posthog.reset();
    setUser(null);
    setIsAuthenticated(false);
    setToken(null);
    // Clear the httpOnly auth_token/refresh_token cookies server-side too —
    // without this the session cookies stayed valid after "logout" since
    // this function only ever cleared localStorage.
    api.post('/auth/logout').catch(() => {});
  }, []);

  // Axios / api layer signals invalid JWT — update React state without reload loops
  useEffect(() => {
    const onClear = () => logout();
    window.addEventListener(CLEAR_AUTH_EVENT, onClear);
    return () => window.removeEventListener(CLEAR_AUTH_EVENT, onClear);
  }, [logout]);

  // Recovery routes: always clear session (SPA navigation to forgot/reset, not only first paint)
  // Other routes: hydrate from localStorage once per app load
  useEffect(() => {
    const path = location.pathname;

    if (isPasswordRecoveryPath(path)) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
      hydratedRef.current = true;
      return;
    }

    if (!hydratedRef.current) {
      hydratedRef.current = true;
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      if (storedToken && storedUser) {
        try {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          setIsAuthenticated(true);
        } catch {
          logout();
        }
        setIsLoading(false);
        return;
      }

      // No local token — this app may still have a valid shared-domain
      // refresh cookie from logging in on another *.dwello.ikxano.com app.
      // attemptSilentRefresh needs no existing token (reads the httpOnly
      // refresh cookie via withCredentials); on success it also stores the
      // fresh access token to localStorage. Fetch the user afterward — a
      // real `token` in state matters here, not just `isAuthenticated`:
      // MessagingContext/NotificationContext read `token` directly and skip
      // fetching entirely (or throw) when it's null.
      attemptSilentRefresh()
        .then((freshToken) => {
          if (!freshToken) return null;
          setToken(freshToken);
          return api.get('/auth/profile');
        })
        .then((res) => {
          const hydratedUser = res?.data?.data?.user;
          if (hydratedUser) {
            localStorage.setItem('user', JSON.stringify(hydratedUser));
            setUser(hydratedUser);
            setIsAuthenticated(true);
          }
        })
        .catch(() => {
          // No valid session anywhere — genuinely logged out, nothing to do.
        })
        .finally(() => setIsLoading(false));
    }
  }, [location.pathname, logout]);

  const login = (loginToken: string, userData: User) => {
    if (loginToken) {
      localStorage.setItem('token', loginToken);
      setToken(loginToken);
    }
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
  };

  const updateProfile = (newToken: string, userData: User) => {
    if (newToken) {
      localStorage.setItem('token', newToken);
      setToken(newToken);
    }
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  // Tie analytics events to a real account once we know who the user is —
  // covers explicit login() and the silent-refresh hydration path alike,
  // since both just end up calling setUser().
  useEffect(() => {
    if (user?.id) {
      posthog.identify(user.id, { email: user.email, role: user.role });
    }
  }, [user?.id, user?.email, user?.role]);

  return (
    <AuthContext.Provider value={{ user, login, updateProfile, logout, isAuthenticated, token, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
